// Save the logo opening and end on this video so the next ones can start with them.

import React, { useCallback, useEffect, useState } from 'react';
import { Bookmark, Check, Loader2, Trash2 } from 'lucide-react';
import { applyBrandPlates, brandPlatesOf, type EditorState } from '@rehub/propertycut-timeline';
import type { AgentData } from '../../types';
import type { EditorSelection } from './model';
import {
  deleteBrandKit,
  fetchBrandKit,
  saveBrandKit,
  type SavedBrandKit,
} from '../../services/supabase';

interface SavedBrandingProps {
  userId: string;
  state: EditorState;
  agent: AgentData;
  onEdit: (recipe: (state: EditorState) => EditorState) => void;
  onAgent: (agent: AgentData) => void;
  onSelect: (selection: EditorSelection) => void;
}

type Notice = { tone: 'ok' | 'error'; text: string } | null;

function kitSummary(kit: SavedBrandKit): string {
  if (kit.opening && kit.ending) return 'Opening and end saved';
  if (kit.opening) return 'Opening saved';
  if (kit.ending) return 'End saved';
  return 'Logo saved';
}

export default function SavedBranding({
  userId,
  state,
  agent,
  onEdit,
  onAgent,
  onSelect,
}: SavedBrandingProps) {
  const [kit, setKit] = useState<SavedBrandKit | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<'save' | 'apply' | 'remove' | null>(null);
  const [notice, setNotice] = useState<Notice>(null);
  const plates = brandPlatesOf(state);
  const canSave = Boolean(plates.opening || plates.ending);

  const load = useCallback(async () => {
    try {
      setKit(await fetchBrandKit(userId));
    } catch (err) {
      console.warn('[propertycut] saved branding load failed:', err);
      setNotice({ tone: 'error', text: 'Could not load your saved opening and end.' });
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    if (!canSave || busy) return;
    setBusy('save');
    setNotice(null);
    try {
      const saved = await saveBrandKit(userId, {
        logoUrl: agent.logo_url,
        opening: plates.opening,
        ending: plates.ending,
      });
      setKit(saved);
      setNotice({ tone: 'ok', text: 'Saved. New videos will start with this opening and end.' });
    } catch (err: any) {
      setNotice({ tone: 'error', text: err?.message || 'Could not save the opening and end.' });
    } finally {
      setBusy(null);
    }
  };

  const apply = () => {
    if (!kit || busy) return;
    setBusy('apply');
    setNotice(null);
    let openingId = '';
    onEdit((current) => {
      const next = applyBrandPlates(current, { opening: kit.opening, ending: kit.ending });
      openingId = next.clips[0]?.blank ? next.clips[0].id : '';
      return next;
    });
    if (kit.logoUrl) onAgent({ ...agent, logo_url: kit.logoUrl });
    if (openingId) onSelect({ type: 'clip', id: openingId });
    setNotice({ tone: 'ok', text: 'Added your saved opening and end.' });
    setBusy(null);
  };

  const remove = async () => {
    if (!kit || busy) return;
    setBusy('remove');
    setNotice(null);
    try {
      await deleteBrandKit(userId);
      setKit(null);
      setNotice({ tone: 'ok', text: 'Saved opening and end removed.' });
    } catch (err: any) {
      setNotice({ tone: 'error', text: err?.message || 'Could not remove the saved opening and end.' });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-3 space-y-2">
      <p className="text-xs font-medium text-slate-200 flex items-center gap-1.5">
        <Bookmark className="w-3.5 h-3.5 text-teal-300" />
        Saved branding
      </p>
      <p className="text-[11px] text-slate-500">
        Save this logo opening and end once. Every new video starts with them.
      </p>
      {loading ? (
        <p className="text-[11px] text-slate-500 flex items-center gap-1.5">
          <Loader2 className="w-3 h-3 animate-spin" />
          Loading…
        </p>
      ) : kit && (
        <div className="flex items-center gap-2">
          {kit.logoUrl && (
            <div className="h-8 w-12 rounded border border-slate-700 bg-slate-950 grid place-items-center overflow-hidden shrink-0">
              <img src={kit.logoUrl} alt="" className="max-h-6 max-w-10 object-contain" />
            </div>
          )}
          <p className="text-[11px] text-slate-300">{kitSummary(kit)}</p>
        </div>
      )}
      <button
        type="button"
        disabled={!canSave || busy !== null}
        onClick={() => void save()}
        className="flex w-full items-center justify-center gap-1.5 rounded-md border border-slate-700 px-3 py-2 text-xs text-slate-100 hover:border-teal-400/70 disabled:opacity-40 disabled:hover:border-slate-700"
      >
        {busy === 'save' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5 text-teal-300" />}
        {kit ? 'Update saved opening & end' : 'Save this opening & end'}
      </button>
      {!canSave && !loading && (
        <p className="text-[11px] text-slate-500">Add an opening or end plate above, then save it.</p>
      )}
      {kit && (
        <>
          <button
            type="button"
            disabled={busy !== null}
            onClick={apply}
            className="w-full rounded-md bg-teal-400 py-2 text-xs font-semibold text-slate-950 hover:bg-teal-300 disabled:opacity-40"
          >
            Use on this video
          </button>
          <button
            type="button"
            disabled={busy !== null}
            onClick={() => void remove()}
            className="flex w-full items-center justify-center gap-1.5 text-[11px] text-slate-500 hover:text-red-300 disabled:opacity-40"
          >
            {busy === 'remove' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
            Remove saved branding
          </button>
        </>
      )}
      {notice && (
        <p className={`text-[11px] ${notice.tone === 'ok' ? 'text-teal-300' : 'text-amber-200'}`}>{notice.text}</p>
      )}
    </div>
  );
}
